import {Component, ElementRef, Injector, ViewChild} from '@angular/core';
import {DatePipe} from "@angular/common";
import {Router} from "@angular/router";
import {Athlete} from "../../models/athlete";
import {loadItalianProvinces, Province, residenceFromLookup} from "../../resources/provinces";
import {AddressLookupResult} from "../../services/address-lookup/address-lookup.service";
import {AthleteService} from "../../services/athlete/athlete.service";
import {BaseComponent} from "../base/base.component";
import {
    fiscalCodeBirthDateMismatch, minBornIsoDate, normalizeFiscalCode, normalizeSpaces, todayIsoDate,
    validateBornDate, validateEmail, validateFiscalCode, validateName, validatePhone, validateRequiredText
} from "../../resources/person-validation";

type AthleteField = 'firstName' | 'lastName' | 'bornDate' | 'bornCity' | 'citizenshipType' | 'fiscalCode'
    | 'address' | 'city' | 'province' | 'email' | 'phone'
    | 'alreadyPlayedInCompany' | 'firstTimeAgonistic';


@Component({
    selector: 'app-athlete-new',
    templateUrl: './athlete-new.component.html'
})
export class AthleteNewComponent extends BaseComponent {

    @ViewChild('stepHeading') stepHeading?: ElementRef<HTMLElement>;

    readonly steps: Array<{ title: string; description: string; fields: AthleteField[] }> = [
        {
            title: 'Anagrafica',
            description: 'Inserisci i dati dell\'atleta come risultano dal documento d\'identità.',
            fields: ['firstName', 'lastName', 'bornDate', 'bornCity', 'citizenshipType', 'fiscalCode'],
        },
        {
            title: 'Residenza e contatti',
            description: 'Servono alla società per il tesseramento e per abilitare i pagamenti.',
            fields: ['address', 'city', 'province', 'email', 'phone'],
        },
        {
            title: 'Esperienza sportiva',
            description: 'Due domande per aiutare la società a completare il tesseramento.',
            fields: ['alreadyPlayedInCompany', 'firstTimeAgonistic'],
        },
        {
            title: 'Riepilogo',
            description: 'Controlla i dati prima di inviare la richiesta di iscrizione.',
            fields: [],
        },
    ];

    readonly citizenshipTypes = [
        {id: 'italiano', name: 'Italiano'},
        {id: 'europeo_non_ita', name: 'Europeo non ITA'},
        {id: 'extra_comunitario_eu', name: 'Extra comunitario EU'}
    ];

    athlete: Athlete = new Athlete();
    provinces: Province[] = [];
    summaryGroups: Array<{ step: number; rows: Array<{ label: string; value: string }> }> = [];
    currentStep = 0;
    submitting = false;
    checkingFiscalCode = false;
    fiscalCodeAlreadyRegistered = false;
    readonly today = todayIsoDate();
    readonly minBornDate = minBornIsoDate();

    private touched = new Set<AthleteField>();
    private submittedSteps = new Set<number>();
    private readonly companyId = localStorage.getItem('wisper_companyId') ?? '';

    constructor(public athleteService: AthleteService, private router: Router, public serviceInjector: Injector) {
        super(serviceInjector);
        loadItalianProvinces().then(provinces => this.provinces = provinces);
    }

    get isLastStep(): boolean {
        return this.currentStep === this.steps.length - 1;
    }

    get progressPercent(): number {
        return ((this.currentStep + 1) / this.steps.length) * 100;
    }

    get fiscalCodeLength(): number {
        return (this.athlete.fiscalCode || '').length;
    }

    get citizenshipLabel(): string {
        return this.citizenshipTypes.find(type => type.id === this.athlete.citizenshipType)?.name ?? '—';
    }

    /** Soft check: the fiscal code is valid but its encoded birth date differs from the one entered. */
    get fiscalCodeBirthDateMismatch(): boolean {
        return fiscalCodeBirthDateMismatch(this.athlete.fiscalCode, this.athlete.bornDate);
    }

    errorFor(field: AthleteField): string | null {
        const a = this.athlete;
        switch (field) {
            case 'firstName':
                return validateName(a.firstName, 'Il nome');
            case 'lastName':
                return validateName(a.lastName, 'Il cognome');
            case 'bornDate':
                return validateBornDate(a.bornDate);
            case 'bornCity':
                return validateRequiredText(a.bornCity, 'Inserisci il luogo di nascita.');
            case 'citizenshipType':
                return a.citizenshipType ? null : 'Seleziona la nazionalità.';
            case 'fiscalCode':
                return validateFiscalCode(a.fiscalCode)
                    ?? (this.fiscalCodeAlreadyRegistered ? 'Questo atleta risulta già registrato in questa società.' : null);
            case 'address':
                return validateRequiredText(a.address, 'Inserisci l\'indirizzo di residenza.', 3);
            case 'city':
                return validateRequiredText(a.city, 'Inserisci la città di residenza.');
            case 'province':
                return a.province ? null : 'Seleziona la provincia.';
            case 'email':
                return validateEmail(a.email);
            case 'phone':
                return validatePhone(a.phone);
            case 'alreadyPlayedInCompany':
                return a.alreadyPlayedInCompany === 0 || a.alreadyPlayedInCompany === 1 ? null : 'Seleziona una risposta.';
            case 'firstTimeAgonistic':
                return a.firstTimeAgonistic === '0' || a.firstTimeAgonistic === '1' ? null : 'Seleziona una risposta.';
        }
    }

    showError(field: AthleteField): string | null {
        const visible = this.touched.has(field) || this.submittedSteps.has(this.currentStep);
        return visible ? this.errorFor(field) : null;
    }

    markTouched(field: AthleteField): void {
        this.touched.add(field);
    }

    normalizeText(field: 'firstName' | 'lastName' | 'bornCity' | 'city' | 'email' | 'previousCompanyName'): void {
        this.athlete[field] = normalizeSpaces(this.athlete[field]);
    }

    onFiscalCodeInput(value: string): void {
        this.athlete.fiscalCode = normalizeFiscalCode(value);
        this.fiscalCodeAlreadyRegistered = false;
    }

    onAddressSelected(result: AddressLookupResult): void {
        const residence = residenceFromLookup(result, this.provinces);
        this.athlete.address = residence.address;
        if (residence.city) this.athlete.city = residence.city;
        if (residence.province) this.athlete.province = residence.province;
        (['address', 'city', 'province'] as AthleteField[]).forEach(field => this.markTouched(field));
    }

    onPhoneChange(value: string): void {
        this.athlete.phone = value;
        this.markTouched('phone');
    }

    setAlreadyPlayed(value: number): void {
        this.athlete.alreadyPlayedInCompany = value;
        if (value === 1) {
            this.athlete.previousCompanyName = '';
        }
        this.markTouched('alreadyPlayedInCompany');
    }

    stepIsValid(step: number): boolean {
        return this.steps[step].fields.every(field => !this.errorFor(field));
    }

    async next(): Promise<void> {
        this.submittedSteps.add(this.currentStep);
        if (!this.stepIsValid(this.currentStep)) {
            this.focusFirstInvalidField();
            return;
        }
        if (this.currentStep === 0 && !(await this.verifyFiscalCodeIsFree())) {
            this.focusFirstInvalidField();
            return;
        }
        this.goToStep(this.currentStep + 1);
    }

    back(): void {
        if (this.currentStep === 0) {
            this.router.navigate(['/users/dashboard']);
            return;
        }
        this.goToStep(this.currentStep - 1);
    }

    /** Only completed steps (or the current one) can be reached from the stepper. */
    canJumpTo(step: number): boolean {
        return step <= this.currentStep || this.steps.slice(0, step).every((_, index) => this.stepIsValid(index));
    }

    goToStep(step: number): void {
        if (step < 0 || step >= this.steps.length || !this.canJumpTo(step)) {
            return;
        }
        this.currentStep = step;
        if (this.isLastStep) {
            this.summaryGroups = this.buildSummary();
        }
        window.scrollTo({top: 0, behavior: 'smooth'});
        setTimeout(() => this.stepHeading?.nativeElement.focus());
    }

    async submit(): Promise<void> {
        const firstInvalidStep = this.steps.findIndex((_, index) => !this.stepIsValid(index));
        if (firstInvalidStep !== -1) {
            this.submittedSteps.add(firstInvalidStep);
            this.goToStep(firstInvalidStep);
            return;
        }
        if (this.submitting) {
            return;
        }

        this.submitting = true;
        this.showSpinner();
        try {
            if (!(await this.verifyFiscalCodeIsFree())) {
                this.submittedSteps.add(0);
                this.goToStep(0);
                return;
            }
            const payload = new Athlete(this.athlete);
            payload.email = (payload.email || '').trim().toLowerCase();
            payload.previousCompanyName = this.athlete.alreadyPlayedInCompany === 0 ? this.athlete.previousCompanyName : '';
            payload.companyId = this.companyId;
            payload.userId = localStorage.getItem('wisper_userId') ?? '';

            const response = await this.athleteService.addNewAthlete(payload);
            if (response?.success) {
                this.showSuccessMessage('Atleta iscritto con successo!');
                this.router.navigate(['/users/dashboard']);
            } else {
                this.showErrorMessage('Errore, impossibile aggiungere l\'atleta. Riprova.');
            }
        } catch (e) {
            console.error('error registration athlete: ', e);
            this.showErrorMessage('Errore, impossibile aggiungere l\'atleta. Riprova.');
        } finally {
            this.submitting = false;
            this.hideSpinner();
        }
    }

    private buildSummary(): Array<{ step: number; rows: Array<{ label: string; value: string }> }> {
        const a = this.athlete;
        const yesNo = (value: boolean) => value ? 'Sì' : 'No';
        const experience = [
            {label: 'Ha già giocato in questa società', value: yesNo(a.alreadyPlayedInCompany === 1)},
            {label: 'Prima esperienza agonistica', value: yesNo(a.firstTimeAgonistic === '1')},
        ];
        if (a.alreadyPlayedInCompany === 0) {
            experience.splice(1, 0, {label: 'Ultima società', value: a.previousCompanyName || '—'});
        }
        return [
            {step: 0, rows: [
                {label: 'Nome e cognome', value: `${a.firstName} ${a.lastName}`},
                {label: 'Data di nascita', value: new DatePipe('it').transform(a.bornDate, 'dd/MM/yyyy') ?? a.bornDate},
                {label: 'Luogo di nascita', value: a.bornCity},
                {label: 'Nazionalità', value: this.citizenshipLabel},
                {label: 'Codice fiscale', value: a.fiscalCode},
            ]},
            {step: 1, rows: [
                {label: 'Residenza', value: [a.address, a.city].filter(Boolean).join(', ') + (a.province ? ` (${a.province})` : '')},
                {label: 'Email', value: a.email || ''},
                {label: 'Telefono', value: a.phone || ''},
            ]},
            {step: 2, rows: experience},
        ];
    }

    private async verifyFiscalCodeIsFree(): Promise<boolean> {
        this.checkingFiscalCode = true;
        try {
            const response: any = await this.athleteService.checkExistingAthlete({
                fiscalCode: this.athlete.fiscalCode,
                companyId: this.companyId,
            });
            this.fiscalCodeAlreadyRegistered = !!response?.data;
        } finally {
            this.checkingFiscalCode = false;
        }
        return !this.fiscalCodeAlreadyRegistered;
    }

    private focusFirstInvalidField(): void {
        const field = this.steps[this.currentStep].fields.find(f => this.errorFor(f));
        if (field) {
            setTimeout(() => {
                const target = document.querySelector<HTMLElement>(`[data-field="${field}"]`);
                (target?.matches('input, select, textarea') ? target : target?.querySelector<HTMLElement>('input') ?? target)?.focus();
            });
        }
    }

}
